import { isAxiosError } from "axios";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { axiosInstance } from "@/lib/api";
import { POLL_IDLE_MS, clampWhenHidden } from "@/lib/realtime";
import type {
  WorkshopArtifact,
  WorkshopArtifactListItem,
  WorkshopArtifactRevision,
} from "@/types/workshop.type";
import {
  workshopArtifactKey,
  workshopArtifactRevisionsKey,
  workshopArtifactsKey,
  workshopArtifactsListKey,
} from "./workshopKeys";

/**
 * One project's artifact list, as a reusable read. The Projects home reads
 * every project's list through the same key the room uses, so the two share
 * one cache entry and never disagree.
 */
export function workshopArtifactsQuery(projectId: string | null, includeArchived: boolean) {
  return queryOptions({
    queryKey: workshopArtifactsListKey(projectId, includeArchived),
    queryFn: async () => {
      const res = await axiosInstance.get<{
        artifacts: WorkshopArtifactListItem[];
      }>(
        `/workshop/projects/${projectId}/artifacts?includeArchived=${includeArchived}`,
      );
      return res.data.artifacts;
    },
    enabled: !!projectId,
  });
}

export function useWorkshopArtifacts(
  projectId: string | null,
  includeArchived: boolean,
) {
  return useQuery({
    ...workshopArtifactsQuery(projectId, includeArchived),
    // Deployed agents write artifacts on their own schedule — idle floor.
    refetchInterval: () => clampWhenHidden(POLL_IDLE_MS),
  });
}

/** One artifact with its content, as a reusable read (an output's Export reads it too). */
export function workshopArtifactQuery(artifactId: string | null) {
  return queryOptions({
    queryKey: workshopArtifactKey(artifactId),
    queryFn: async () => {
      const res = await axiosInstance.get<{ artifact: WorkshopArtifact }>(
        `/workshop/artifacts/${artifactId}`,
      );
      return res.data.artifact;
    },
    enabled: !!artifactId,
  });
}

export function useWorkshopArtifact(artifactId: string | null) {
  return useQuery({
    ...workshopArtifactQuery(artifactId),
    refetchInterval: () => clampWhenHidden(POLL_IDLE_MS),
  });
}

export function useWorkshopArtifactRevisions(artifactId: string | null) {
  return useQuery({
    queryKey: workshopArtifactRevisionsKey(artifactId),
    queryFn: async () => {
      const res = await axiosInstance.get<{
        revisions: WorkshopArtifactRevision[];
      }>(`/workshop/artifacts/${artifactId}/revisions`);
      return res.data.revisions;
    },
    enabled: !!artifactId,
  });
}

/** Someone else saved the artifact after this editor opened it. */
export type ArtifactEditConflict = {
  /** The version it is at now. */
  currentVersion: number;
  /** Who saved it, when the server could say. */
  by: string | null;
};

/** The conflict in a refused save (409 WORKSHOP_ARTIFACT_STALE), else null. */
export function readArtifactConflict(error: unknown): ArtifactEditConflict | null {
  if (!isAxiosError(error) || error.response?.status !== 409) return null;
  const data = error.response.data as
    | {
        code?: string;
        currentVersion?: number;
        lastEditedBy?: { userName?: string | null } | null;
      }
    | undefined;
  if (data?.code !== "WORKSHOP_ARTIFACT_STALE") return null;
  return { currentVersion: data.currentVersion ?? 0, by: data.lastEditedBy?.userName ?? null };
}

export function useWorkshopArtifactMutations(projectId: string | null) {
  const qc = useQueryClient();

  const invalidateArtifact = (artifactId: string) => {
    void qc.invalidateQueries({ queryKey: workshopArtifactsKey(projectId) });
    void qc.invalidateQueries({ queryKey: workshopArtifactKey(artifactId) });
    void qc.invalidateQueries({
      queryKey: workshopArtifactRevisionsKey(artifactId),
    });
  };

  const updateArtifact = useMutation({
    mutationFn: async (vars: {
      artifactId: string;
      content: string;
      title?: string;
      note?: string;
      /**
       * The version the editor opened. With collaborators, a save on top of
       * someone else's newer one is refused (see readArtifactConflict).
       */
      baseVersion?: number;
    }) => {
      const { artifactId, ...body } = vars;
      const res = await axiosInstance.put<{ artifact: WorkshopArtifact }>(
        `/workshop/artifacts/${artifactId}`,
        body,
      );
      return res.data.artifact;
    },
    onSuccess: (updated) => invalidateArtifact(updated.id),
  });

  /**
   * Archive or restore, optimistic: the row leaves Outputs (or comes back) at
   * once. A refusal puts back only this row, so the Undo toast and a second
   * archive in flight never undo each other.
   */
  const archiveMutationKey = ["workshopArtifactArchive", projectId];
  const archiveArtifact = useMutation({
    mutationKey: archiveMutationKey,
    mutationFn: async (vars: { artifactId: string; archived: boolean }) => {
      const res = await axiosInstance.post<{ artifact: WorkshopArtifact }>(
        `/workshop/artifacts/${vars.artifactId}/archive`,
        { archived: vars.archived },
      );
      return res.data.artifact;
    },
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: workshopArtifactsKey(projectId) });
      const status = vars.archived ? "archived" : "active";
      const activeKey = workshopArtifactsListKey(projectId, false);
      const allKey = workshopArtifactsListKey(projectId, true);
      const row =
        qc.getQueryData<WorkshopArtifactListItem[]>(allKey)?.find((a) => a.id === vars.artifactId) ??
        qc.getQueryData<WorkshopArtifactListItem[]>(activeKey)?.find((a) => a.id === vars.artifactId) ??
        null;
      qc.setQueryData<WorkshopArtifactListItem[]>(allKey, (list) =>
        list?.map((a) => (a.id === vars.artifactId ? { ...a, status } : a)),
      );
      qc.setQueryData<WorkshopArtifactListItem[]>(activeKey, (list) => {
        if (!list) return list;
        if (vars.archived) return list.filter((a) => a.id !== vars.artifactId);
        if (!row || list.some((a) => a.id === row.id)) return list;
        return [{ ...row, status }, ...list];
      });
      return { row };
    },
    onError: (_error, _vars, context) => {
      const row = context?.row;
      if (!row) return;
      const activeKey = workshopArtifactsListKey(projectId, false);
      qc.setQueryData<WorkshopArtifactListItem[]>(workshopArtifactsListKey(projectId, true), (list) =>
        list?.map((a) => (a.id === row.id ? row : a)),
      );
      qc.setQueryData<WorkshopArtifactListItem[]>(activeKey, (list) => {
        if (!list) return list;
        const without = list.filter((a) => a.id !== row.id);
        return row.status === "archived" ? without : [row, ...without];
      });
    },
    // Reconcile once the last archive or restore has settled: an earlier
    // refetch would briefly bring back a row a later archive just took out.
    onSettled: (_data, _error, vars) => {
      void qc.invalidateQueries({ queryKey: workshopArtifactKey(vars.artifactId) });
      if (qc.isMutating({ mutationKey: archiveMutationKey }) > 1) return;
      invalidateArtifact(vars.artifactId);
    },
  });

  return { updateArtifact, archiveArtifact };
}
